import { useMemo } from "react";
import { ApolloError } from "@apollo/client";
import {
  Alert,
  AlertIcon,
  AspectRatio,
  Center,
  Link,
  ListItem,
  CircularProgress,
  Tooltip,
} from "@chakra-ui/react";
import { darken } from "polished";
import colors from "theme/foundations/colors";
import DropzoneWell from "components/Shared/DropzoneWell";
import { FileUploadWrappedError } from "utils/files/uploads/errors";
import UserError from "utils/UserError";
import BrandDocumentFileUpload, {
  CreatedBrandDocument,
} from "utils/files/uploads/BrandDocumentFileUpload";
import { useDocumentFileUpload } from "utils/files/uploads/DocumentFileUpload";
import useMultipleBrandDocumentUpload from "utils/files/uploads/useMultipleBrandDocumentUpload";
import { asyncVoid } from "utils/asyncVoidHandler";
import FolderDocumentCard from "./FolderDocumentCard";

function isUserError(
  error: Error | undefined,
): error is FileUploadWrappedError<UserError | ApolloError> {
  return (
    !!error &&
    error instanceof FileUploadWrappedError &&
    (error.originalError instanceof UserError ||
      error.originalError instanceof ApolloError)
  );
}

type UploadCardProps = {
  upload: BrandDocumentFileUpload;
};

function UploadCard({ upload }: UploadCardProps) {
  const { loading, progress, error } = useDocumentFileUpload(upload);
  const { createdDocument } = upload;
  const userError = isUserError(error) ? error : undefined;
  return (
    <FolderDocumentCard
      isDisabled={!createdDocument}
      filename={upload.fileUpload.file.name}
      documentId={createdDocument ? createdDocument?.id : undefined}
      isArchived={createdDocument?.isArchived}
    >
      <Tooltip
        hasArrow
        openDelay={300}
        placement="top"
        isDisabled={!userError}
        label={userError?.message}
      >
        <Alert
          status={(() => {
            if (createdDocument) return "success";
            if (userError) return "error";
            return "info";
          })()}
          position="absolute"
          top={0}
          left={0}
          right={0}
          zIndex={1}
          padding="2"
          fontSize=".8em"
        >
          {(!!createdDocument || !!userError) && <AlertIcon h="4" />}

          {(() => {
            if (loading) return "Uploading…";
            if (createdDocument) return "Uploaded";
            if (userError) return "Failed. ";
            return "Pending";
          })()}

          {!!userError && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link
              as="button"
              onClick={() => asyncVoid(upload.retryDocumentUpload())}
              marginLeft="1"
            >
              Retry?
            </Link>
          )}
        </Alert>
      </Tooltip>

      {loading && (
        <Center
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          zIndex={1}
        >
          <CircularProgress
            value={progress && progress * 100}
            isIndeterminate={progress === 1}
            size="4rem"
            color="magenta.dark"
          />
        </Center>
      )}
    </FolderDocumentCard>
  );
}

type Props = {
  folderId: string;
  onUploaded?: (data: CreatedBrandDocument) => void;
};

export default function FolderDocumentUpload({ folderId, onUploaded }: Props) {
  const uploadOptions = useMemo(
    () => ({
      additionalBrandDocumentData: { documentFolderId: folderId },
    }),
    [folderId],
  );

  const { droppedFiles, dropzone } = useMultipleBrandDocumentUpload({
    uploadOptions,
    onUploaded,
  });
  return (
    <>
      <AspectRatio as={ListItem} ratio={3 / 4}>
        <DropzoneWell
          dropzone={dropzone}
          backgroundColor="softBlue"
          minHeight="10rem"
          color="white"
          fontSize="3rem"
          fontWeight={200}
          _hover={{ backgroundColor: darken(0.05)(colors.softBlue) }}
          _valid={{ borderColor: "magenta.dark" }}
        >
          +
        </DropzoneWell>
      </AspectRatio>
      {droppedFiles.map((upload) => (
        <UploadCard key={upload.fileUpload.id} upload={upload} />
      ))}
    </>
  );
}
